import { useMemo, useState } from "react";

import type { Status } from "api/types/aba";
import type { ClinicalPhase } from "../../shared/types";
import type { GoalTarget } from "./index";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type TargetFilterGroup = "phase" | "status";

export interface TargetFilterOption {
  /** Unique identifier e.g. "phase:baseline" */
  id: string;
  /** Human-readable label e.g. "Baseline" */
  label: string;
  /** Which filter group this option belongs to */
  group: TargetFilterGroup;
}

export interface TargetActiveFilter {
  /** Unique identifier — matches TargetFilterOption.id */
  id: string;
  /** Human-readable label shown inside the chip */
  label: string;
}

// ---------------------------------------------------------------------------
// Label maps
// ---------------------------------------------------------------------------

const PHASE_LABELS: Record<ClinicalPhase, string> = {
  baseline: "Baseline",
  intervention: "Intervention",
  generalization: "Generalization",
  maintenance: "Maintenance",
};

const STATUS_LABELS: Record<Status, string> = {
  "not-active": "Not Active",
  "in-progress": "In Progress",
  "on-hold": "On Hold",
  met: "Met",
  discontinued: "Discontinued",
  archived: "Archived",
};

// ---------------------------------------------------------------------------
// Result shape
// ---------------------------------------------------------------------------

export interface UseTargetFiltersResult {
  /** Targets after all active filters are applied — ready to render */
  filteredTargets: GoalTarget[];
  /** Total count before any filtering — used for "X of Y" label */
  totalCount: number;
  /** Currently active filter chips */
  activeFilters: TargetActiveFilter[];
  /** Smart filter options derived from the current targets data */
  filterOptions: TargetFilterOption[];
  /** Count of targets that have a masteryHint (i.e. need clinical attention) */
  needsAttentionCount: number;
  /** Whether the "Needs Attention" quick-filter is currently active */
  isNeedsAttentionActive: boolean;
  onAddFilter: (option: TargetFilterOption) => void;
  onDeleteFilter: (filterId: string) => void;
  onClearFilters: () => void;
  onNeedsAttentionClick: () => void;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Encapsulates filter state, smart option computation, and filtering logic
 * for the GoalTargetsList. Consumers pass in raw targets and get back filtered
 * targets + the props needed to drive the filter toolbar.
 *
 * Filter behaviour:
 * - Phase and Status are independent groups: AND across groups, OR within.
 * - "Needs Attention" is an additive quick-filter: further narrows by masteryHint.
 * - Smart options: only values present in the current targets appear in the menu.
 */
export function useTargetFilters(targets: GoalTarget[]): UseTargetFiltersResult {
  const [activeFilters, setActiveFilters] = useState<TargetActiveFilter[]>([]);
  const [isNeedsAttentionActive, setIsNeedsAttentionActive] = useState(false);

  const filterOptions = useMemo<TargetFilterOption[]>(() => {
    if (!targets.length) return [];

    const phases = new Set<ClinicalPhase>();
    const statuses = new Set<Status>();

    targets.forEach((t) => {
      if (t.clinicalPhase) phases.add(t.clinicalPhase);
      if (t.status) statuses.add(t.status as Status);
    });

    return [
      ...[...phases].map((p) => ({
        id: `phase:${p}`,
        label: PHASE_LABELS[p] ?? p,
        group: "phase" as TargetFilterGroup,
      })),
      ...[...statuses].map((s) => ({
        id: `status:${s}`,
        label: STATUS_LABELS[s] ?? s,
        group: "status" as TargetFilterGroup,
      })),
    ];
  }, [targets]);

  const filteredTargets = useMemo(() => {
    const byGroup: Record<TargetFilterGroup, string[]> = { phase: [], status: [] };

    activeFilters.forEach(({ id }) => {
      const [group, value] = id.split(":") as [TargetFilterGroup, string];
      byGroup[group].push(value);
    });

    return targets.filter((t) => {
      if (byGroup.phase.length && !byGroup.phase.includes(t.clinicalPhase ?? "")) return false;
      if (byGroup.status.length && !byGroup.status.includes(t.status)) return false;
      if (isNeedsAttentionActive && !t.masteryHint) return false;
      return true;
    });
  }, [targets, activeFilters, isNeedsAttentionActive]);

  const needsAttentionCount = useMemo(
    () => targets.filter((t) => Boolean(t.masteryHint)).length,
    [targets]
  );

  return {
    filteredTargets,
    totalCount: targets.length,
    activeFilters,
    filterOptions,
    needsAttentionCount,
    isNeedsAttentionActive,
    onAddFilter: (option) =>
      setActiveFilters((prev) =>
        prev.some((f) => f.id === option.id)
          ? prev
          : [...prev, { id: option.id, label: option.label }]
      ),
    onDeleteFilter: (filterId) => setActiveFilters((prev) => prev.filter((f) => f.id !== filterId)),
    onClearFilters: () => {
      setActiveFilters([]);
      setIsNeedsAttentionActive(false);
    },
    onNeedsAttentionClick: () => setIsNeedsAttentionActive((prev) => !prev),
  };
}
